"use client";

import ProfileCardMenu from "@/layouts/carbon/profile-card-menu";
import cn from "@/utils/class-names";
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import { Link } from "react-router-dom";
import SimpleBar from "simplebar-react";
import { CarbonSidebarMenu } from "./carbon-sidebar-menu";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users.ts";
import { useEffect, useMemo, useState } from "react";
import LogoHeader from "@/components/logo-header";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { TAgencySetting, TDomain } from "@/utilities/types/Setting";
import SettingsRepository from "@/utilities/repositories/Settings";
import { Button } from "rizzui";
import { CgWebsite } from "react-icons/cg";
import { EFeatureType } from "@/utilities/types/AdvanceFeature";
import { useTopBarHeight } from "@/app/shared/agent-dashboard-top-bar/top-bar-height-context";

const ASSET_BASE_URL = import.meta.env.VITE_ASSET_BASE_URL as string;
const DEFAULT_URL_IMAGE =
  "https://property-development.s3.ap-southeast-1.amazonaws.com/assets/images/15733abe-b949-476b-b9a1-142102606bda/dp-992w.webp";

export function CarbonSidebar({ className }: { className?: string }) {
  const { profile } = useProfile();
  const { topBarHeight } = useTopBarHeight();
  const [urlImage, setUrlImage] = useState<string>(DEFAULT_URL_IMAGE);

  const { request: getAgencySetting, response: agencySettingData } = useApi<
    IResponseData<TAgencySetting>
  >({
    request: SettingsRepository.getAgencySetting,
  });

  const previewUrl = useMemo(() => {
    const domain = agencySettingData?.data?.domains?.find(
      (domain: TDomain) => !!domain?.primary,
    );
    if (domain) {
      return (domain as TDomain)?.name || "";
    }
    return "";
  }, [agencySettingData?.data?.domains]);

  const isDirectoryEnabled = useMemo(() => {
    return profile?.userFeatures?.some(
      (item) =>
        item.feature.type === EFeatureType.Directory && item.feature.enabled,
    );
  }, [profile?.id]);

  const showPreviewButton = useMemo(() => {
    return profile?.role === Roles.AGENCY && previewUrl && isDirectoryEnabled;
  }, [profile?.role, previewUrl, isDirectoryEnabled]);

  useEffect(() => {
    getAgencySetting();
    if (profile?.photo?.urls[0])
      setUrlImage(`${ASSET_BASE_URL}${profile.photo.urls[0].url}`);
    if (profile?.photo === null) setUrlImage(DEFAULT_URL_IMAGE);
  }, [profile?.photo, getAgencySetting]);
  return (
    <>
      <aside
        className={cn(
          "fixed flex flex-col justify-between bottom-0 start-0 z-50 h-full w-[292px] border-e-2 border-gray-100 bg-transparent dark:bg-transparent 2xl:w-[292px]",
          className,
        )}
      >
        {topBarHeight > 0 && (
          <div style={{ height: `${topBarHeight}px` }}></div>
        )}
        <div className="sticky top-0 z-40 bg-transparent px-6 pb-5 pt-5 dark:bg-transparent 2xl:px-8 2xl:pt-6">
          <Link to={"/"} aria-label="Site Logo" className="text-gray-800">
            <LogoHeader className="max-w-[200px]" />
          </Link>
        </div>

        <SimpleBar
          className={cn(
            "flex-1 overflow-auto [&_.simplebar-content]:flex [&_.simplebar-content]:h-full [&_.simplebar-content]:flex-col [&_.simplebar-content]:justify-between",
            profile?.role === Roles.ADMIN && "h-[calc(100%-105px)]",
            profile?.role !== Roles.AGENCY && "h-[calc(100%-159px)]",
          )}
          style={
            profile?.role === Roles.AGENCY && showPreviewButton
              ? { height: `calc(100% - ${260 + topBarHeight}px)` }
              : profile?.role === Roles.USER
              ? { height: `calc(100% - ${159 + topBarHeight}px)` }
              : undefined
          }
        >
          <CarbonSidebarMenu />
        </SimpleBar>
        {showPreviewButton && (
          <Link
            to={`https://${previewUrl}`}
            target="_blank"
            className="h-auto block p-6"
          >
            {/* TODO: Link to agent's domain instead of hard code */}

            <Button
              className="h-14 w-full flex justify-start items-center text-xs sm:text-sm font-medium capitalize hover:text-black hover:bg-white border border-solid border-black transition-all"
              size="sm"
            >
              <div className="inline-flex items-center gap-x-2">
                <CgWebsite size={24} />
                Preview Website
              </div>
            </Button>
          </Link>
        )}

        <div className="gap-3 mt-auto bg-gray-0 px-6 pb-1 dark:bg-gray-50">
          <ProfileCardMenu
            title={profile?.firstName + " " + profile?.lastName}
            designation={
              profile?.role === Roles.AGENCY
                ? profile?.company || ""
                : profile?.role
            }
            placement="top"
            image={urlImage}
            avatarClassName="!w-10 !h-10"
            icon={
              <PiDotsThreeVerticalBold
                className={cn(
                  "h-7 w-7 text-gray-400 transition-all group-hover:text-primary",
                )}
              />
            }
            className={cn("px-0 py-0")}
            buttonClassName="border-0 !border-t !border-gray-200 pt-5 px-0 rounded-none"
          />
        </div>
      </aside>
    </>
  );
}
